import cn from 'classnames';
import { useSelector } from 'react-redux';
import TextField from '@guestyci/foundation/TextField';
import { Row, Col } from '@guestyci/foundation/Layout';
import createStyles from '@guestyci/foundation/createStyles';
import defaultTheme from '@guestyci/foundation/theme/defaultTheme';
import useMediaQuery from '@guestyci/foundation/useMediaQuery';

import imgSource from 'assets/images/backgrounds/trial-description-bg.png';
import breakpoints from 'app/shareable/theme/breakpoints';
import { selectTrialPeriod } from 'app/store/selector';
import Range from './Range';
import Offers from './Offers';

const useClasses = createStyles((theme: typeof defaultTheme) => {
  const tablet = breakpoints.max.lg;

  return {
    root: {
      width: '100%',
      display: 'grid',
      gridTemplateColumns: 'minmax(14rem, 1fr) minmax(42rem, 3fr)',
      border: 'none',
      backgroundColor: theme.palette.secondary.default,

      [tablet]: {
        gridTemplateColumns: 'initial',
        gridTemplateRows: 'auto minmax(42rem, 3fr)',
      },
    },
    sideBarContainer: {
      padding: '11.25rem 1.25rem 1.25rem 1.25rem',
      borderRadius: '1.25rem 0 0 1.25rem',

      [tablet]: {
        padding: '2rem 1.25rem',
        borderRadius: '1.25rem 1.25rem 0 0',
      },
    },
    bodyContainer: {
      padding: '50px 60px',
      backgroundColor: theme.palette.background.white,

      [tablet]: {
        padding: '20px',
        borderRadius: '1.25rem 1.25rem 0 0',
      },
    },
    descriptionContainer: {
      width: '20rem',
      backgroundImage: `url(${imgSource}), radial-gradient(327.22% 189.58% at 36.17% 100%, #FAFCFF 0%, #D4EAFF 100%)`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: '85%, cover',
      backgroundPosition: 'center 100%, center calc(100% - 0.875rem)',
      marginBottom: '-1rem',
      minHeight: 400,

      [tablet]: {
        width: '100%',
        backgroundSize: '285px, cover',
        order: 4,
      },
    },
    offersRow: {
      padding: '20px 0',
      gap: 20,

      '& > *:not(:last-child)': {
        flex: '30% 0 1',

        [tablet]: {
          flex: 'initial',
          maxWidth: 400,
        },
      },

      [tablet]: {
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      },
    },
    helpLink: {
      marginTop: '2rem',

      [tablet]: {
        order: 1,
        marginBottom: '2rem',
        textAlign: 'center',
      },
    },
  };
});

interface TrialDialogContentProps {
  trialDuration: number;
  daysLeft: number;
}

const TrialDialogContent = ({
  trialDuration,
  daysLeft,
}: TrialDialogContentProps) => {
  const {
    root,
    helpLink,
    offersRow,
    bodyContainer,
    sideBarContainer,
    descriptionContainer,
  } = useClasses();
  const { subscriptionCancelled } = useSelector(selectTrialPeriod);
  const isTablet = useMediaQuery({ maxWidth: 'lg' });

  const isTrialEnded = daysLeft < 1;

  return (
    <div className={root}>
      <Col className={sideBarContainer}>
        <Range max={trialDuration} value={daysLeft} />
        {isTrialEnded ? (
          <>
            <TextField
              className={cn('mt-4 mb-2', { largeFontSize: !isTablet })}
              variant="h1"
              color="white"
            >
              {subscriptionCancelled
                ? 'Uh-oh! Your subscription has expired'
                : 'Uh-oh! Your free trial has ended!'}
            </TextField>
            <TextField variant="h4" color="white" bold={false}>
              We’ve had to lock your account.
            </TextField>
            <Col spacing={4}>
              <TextField variant="h4" color="white" bold={false}>
                Not to worry, we&apos;ll save your settings.
              </TextField>
              <TextField variant="h4" color="white" bold={false}>
                Subscribe now to keep your account running.
              </TextField>
            </Col>
          </>
        ) : (
          <>
            <TextField
              className={cn('mt-4 mb-2', { largeFontSize: !isTablet })}
              variant="h1"
              color="white"
              display="inline"
            >
              Only {daysLeft} days left in your free trial!
            </TextField>
            <TextField variant="h4" color="white" bold={false}>
              After the free trial ends, your account will be locked. Subscribe
              to keep the tools that make hosting easier.
            </TextField>
          </>
        )}
      </Col>

      <Col className={bodyContainer} span={12}>
        <Col align="center">
          <TextField variant={isTablet ? 'h3' : 'h1'} align="center">
            <span>Choose a plan and</span>
            <span className="text-primary pl-1">
              subscribe to Guesty for Hosts!
            </span>
          </TextField>
        </Col>

        <Row className={offersRow} justify="center" wrap>
          <Col className={cn(descriptionContainer, 'p-4')} spacing={3}>
            <TextField variant={isTablet ? 'h3' : 'h1'}>
              <span className="font-weight-bold">Easier hosting</span>
              <span className="font-weight-light pl-1">
                at your fingertips:
              </span>
            </TextField>
            <ul className="pl-3">
              <li>
                <TextField variant={isTablet ? 'h5' : 'h4'}>
                  Unified inbox for all messages
                </TextField>
              </li>
              <li>
                <TextField variant={isTablet ? 'h5' : 'h4'}>
                  Multi calendar for all bookings
                </TextField>
              </li>
              <li>
                <TextField variant={isTablet ? 'h5' : 'h4'}>
                  On-the-go mobile control
                </TextField>
              </li>
              <li>
                <TextField variant={isTablet ? 'h5' : 'h4'}>
                  Automations to save time, and much more!
                </TextField>
              </li>
            </ul>
          </Col>

          <Offers />
          <Col align="center" fullWidth className={helpLink}>
            <TextField variant="h5">
              Have questions? We’re happy to help. Reach out and
              <a
                rel="noreferrer"
                href="https://help.guestyforhosts.com/hc/en-gb/requests/new"
                className="pl-1 text-primary no-underline"
                target="_blank"
              >
                contact support.
              </a>
            </TextField>
          </Col>
        </Row>
      </Col>
    </div>
  );
};
export default TrialDialogContent;
